import React, { useState, useEffect } from "react";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Switch,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Checkbox,
  FormGroup,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  Typography,
  Alert,
  CircularProgress,
  Grid,
  Tooltip,
  Card,
  CardContent,
  Divider,
  Stack,
  Snackbar,
  Container,
} from "@mui/material";
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  ContentCopy as CloneIcon,
  CheckCircle as CheckCircleIcon,
  DragIndicator as DragIcon,
  Reorder as ReorderIcon,
} from "@mui/icons-material";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import { API_ENDPOINT } from "../../utils/constants";
import { getHeader } from "../../utils/util";

const MUI_ICONS = {
  TrendingUp: "TrendingUp",
  Business: "Business",
  RocketLaunch: "RocketLaunch",
  Home: "Home",
  Star: "Star",
  WorkspacePremium: "WorkspacePremium",
};

const BILLING_CYCLES = ["quarter", "halfYear", "year"];

const PricingPlansManager = () => {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // Dialog states
  const [openDialog, setOpenDialog] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [dialogMode, setDialogMode] = useState("create"); // 'create' or 'edit' or 'clone'

  // Form state
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    features: [],
    newFeature: "",
    popular: false,
    icon: "TrendingUp",
    color: "#128C7E",
    active: true,
    billingCycles: {
      quarter: { price: 0, contactLimit: 0 },
      halfYear: { price: 0, contactLimit: 0 },
      year: { price: 0, contactLimit: 0 },
    },
    messageRates: {
      marketing: 0,
      utility: 0,
    },
  });

  // Fetch plans on component mount
  useEffect(() => {
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    try {
      setLoading(true);

      const response = await fetch(`${API_ENDPOINT}/root/pricing-plans`, {
        method: "GET",
        ...getHeader(),
      });
      const data = await response.json();

      if (response.ok) {
        setPlans(data.plans || []);
      } else {
        setError(data.message || "Failed to fetch plans");
      }
    } catch (err) {
      setError("Error connecting to server");
    } finally {
      setLoading(false);
    }
  };

  const handleOpenCreateDialog = () => {
    setDialogMode("create");
    setFormData({
      name: "",
      description: "",
      features: [],
      newFeature: "",
      popular: false,
      icon: "TrendingUp",
      color: "#128C7E",
      active: true,
      billingCycles: {
        quarter: { price: 0, contactLimit: 0 },
        halfYear: { price: 0, contactLimit: 0 },
        year: { price: 0, contactLimit: 0 },
      },
      messageRates: {
        marketing: 0,
        utility: 0,
      },
    });
    setOpenDialog(true);
  };

  const handleOpenEditDialog = (plan) => {
    setDialogMode("edit");
    setEditingPlan(plan);
    setFormData({
      name: plan.name,
      description: plan.description,
      features: [...plan.features],
      newFeature: "",
      popular: plan.popular,
      icon: plan.icon,
      color: plan.color,
      active: plan.active,
      billingCycles: { ...plan.billingCycles },
      messageRates: { ...plan.messageRates },
    });
    setOpenDialog(true);
  };

  // NEW FUNCTION: Handle Clone Plan
  const handleOpenCloneDialog = (plan) => {
    setDialogMode("clone");
    setEditingPlan(plan);
    setFormData({
      name: `${plan.name} (Copy)`,
      description: plan.description,
      features: [...plan.features],
      newFeature: "",
      popular: false, // Reset popular flag for cloned plan
      icon: plan.icon,
      color: plan.color,
      active: true, // Set active by default for cloned plan
      billingCycles: { ...plan.billingCycles },
      messageRates: { ...plan.messageRates },
    });
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingPlan(null);
  };

  const handleFormChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleNestedFormChange = (parent, field, value) => {
    setFormData((prev) => ({
      ...prev,
      [parent]: {
        ...prev[parent],
        [field]: value,
      },
    }));
  };

  const handleBillingCycleChange = (cycle, field, value) => {
    setFormData((prev) => ({
      ...prev,
      billingCycles: {
        ...prev.billingCycles,
        [cycle]: {
          ...prev.billingCycles[cycle],
          [field]: Number(value) || 0,
        },
      },
    }));
  };

  const handleAddFeature = () => {
    if (formData.newFeature.trim()) {
      setFormData((prev) => ({
        ...prev,
        features: [...prev.features, prev.newFeature.trim()],
        newFeature: "",
      }));
    }
  };

  const handleRemoveFeature = (index) => {
    setFormData((prev) => ({
      ...prev,
      features: prev.features.filter((_, i) => i !== index),
    }));
  };

  const handleSubmit = async () => {
    try {
      const url =
        dialogMode === "create" || dialogMode === "clone"
          ? `${API_ENDPOINT}/root/pricing-plans`
          : `${API_ENDPOINT}/root/pricing-plans/${editingPlan._id}`;

      const method = dialogMode === "edit" ? "PUT" : "POST";

      // Prepare data for submission
      const submitData = {
        ...formData,
        features: formData.features,
      };

      // Remove newFeature from submission
      delete submitData.newFeature;

      const response = await fetch(url, {
        method,
        ...getHeader(),
        body: JSON.stringify(submitData),
      });

      const data = await response.json();

      if (response.ok) {
        setSnackbar({
          open: true,
          message: data.message,
          severity: "success",
        });
        fetchPlans();
        handleCloseDialog();
      } else {
        setSnackbar({
          open: true,
          message: data.message || "Operation failed",
          severity: "error",
        });
      }
    } catch (err) {
      setSnackbar({
        open: true,
        message: "Error connecting to server",
        severity: "error",
      });
    }
  };

  const handleToggleActive = async (planId, currentStatus) => {
    try {
      const response = await fetch(
        `${API_ENDPOINT}/root/pricing-plans/${planId}/toggle-active`,
        {
          method: "PUT",
          ...getHeader(),
        }
      );

      const data = await response.json();

      if (response.ok) {
        setSnackbar({
          open: true,
          message: data.message,
          severity: "success",
        });
        fetchPlans();
      } else {
        setSnackbar({
          open: true,
          message: data.message || "Failed to update status",
          severity: "error",
        });
      }
    } catch (err) {
      setSnackbar({
        open: true,
        message: "Error connecting to server",
        severity: "error",
      });
    }
  };

  const handleDelete = async (planId) => {
    if (!window.confirm("Are you sure you want to delete this plan?")) {
      return;
    }

    try {
      const response = await fetch(
        `${API_ENDPOINT}/root/pricing-plans/${planId}`,
        {
          method: "DELETE",
          ...getHeader(),
        }
      );

      const data = await response.json();

      if (response.ok) {
        setSnackbar({
          open: true,
          message: data.message,
          severity: "success",
        });
        fetchPlans();
      } else {
        setSnackbar({
          open: true,
          message: data.message || "Failed to delete plan",
          severity: "error",
        });
      }
    } catch (err) {
      setSnackbar({
        open: true,
        message: "Error connecting to server",
        severity: "error",
      });
    }
  };

  const handleDragEnd = async (result) => {
    if (!result.destination) return;

    const items = Array.from(plans);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    // Update order in UI immediately for better UX
    const updatedPlans = items.map((item, index) => ({
      ...item,
      order: index,
    }));
    setPlans(updatedPlans);

    // Update order in backend
    try {
      const response = await fetch(
        `${API_ENDPOINT}/root/pricing-plans/${reorderedItem._id}/order`,
        {
          method: "PUT",
          ...getHeader(),
          body: JSON.stringify({ order: result.destination.index }),
        }
      );

      if (!response.ok) {
        // Revert if failed
        fetchPlans();
      }
    } catch (err) {
      // Revert if error
      fetchPlans();
    }
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container sx={{ mt: 3 }}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Typography variant="h6">Pricing Plans Management</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleOpenCreateDialog}
          sx={{ bgcolor: "primary.main" }}
        >
          Add New Plan
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Card>
        <CardContent>
          <DragDropContext onDragEnd={handleDragEnd}>
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell width="50px">Order</TableCell>
                    <TableCell>Name</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Popular</TableCell>
                    <TableCell>Billing Cycles</TableCell>
                    <TableCell>Features</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <Droppable droppableId="plans">
                  {(provided) => (
                    <TableBody
                      ref={provided.innerRef}
                      {...provided.droppableProps}
                    >
                      {plans.map((plan, index) => (
                        <Draggable
                          key={plan._id}
                          draggableId={plan._id}
                          index={index}
                        >
                          {(provided) => (
                            <TableRow
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              sx={{
                                bgcolor: plan.active
                                  ? "background.paper"
                                  : "action.disabledBackground",
                                opacity: plan.active ? 1 : 0.7,
                              }}
                            >
                              <TableCell {...provided.dragHandleProps}>
                                <DragIcon sx={{ cursor: "grab" }} />
                              </TableCell>
                              <TableCell>
                                <Box display="flex" alignItems="center" gap={1}>
                                  <Box
                                    width={40}
                                    height={40}
                                    display="flex"
                                    alignItems="center"
                                    justifyContent="center"
                                    borderRadius={1}
                                    bgcolor={plan.color}
                                    color="white"
                                  >
                                    {plan.icon}
                                  </Box>
                                  <Box>
                                    <Typography variant="subtitle1">
                                      {plan.name}
                                    </Typography>
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                    >
                                      {plan.description}
                                    </Typography>
                                  </Box>
                                </Box>
                              </TableCell>
                              <TableCell>
                                <FormControlLabel
                                  control={
                                    <Switch
                                      checked={plan.active}
                                      onChange={() =>
                                        handleToggleActive(
                                          plan._id,
                                          plan.active
                                        )
                                      }
                                      color="primary"
                                    />
                                  }
                                  label={plan.active ? "Active" : "Inactive"}
                                />
                              </TableCell>
                              <TableCell>
                                {plan.popular && (
                                  <Chip
                                    label="Popular"
                                    color="primary"
                                    size="small"
                                    icon={<CheckCircleIcon />}
                                  />
                                )}
                              </TableCell>
                              <TableCell>
                                <Box>
                                  {Object.entries(plan.billingCycles || {}).map(
                                    ([cycle, data]) => (
                                      <Typography
                                        key={cycle}
                                        variant="caption"
                                        display="block"
                                      >
                                        {cycle}: ₹{data.price} /{" "}
                                        {data.contactLimit} contacts
                                      </Typography>
                                    )
                                  )}
                                </Box>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" noWrap>
                                  {plan.features?.length || 0} features
                                </Typography>
                              </TableCell>
                              <TableCell align="right">
                                <Tooltip title="Edit">
                                  <IconButton
                                    onClick={() => handleOpenEditDialog(plan)}
                                    color="primary"
                                  >
                                    <EditIcon />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Clone">
                                  <IconButton
                                    onClick={() => handleOpenCloneDialog(plan)}
                                    color="info"
                                  >
                                    <CloneIcon />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Delete">
                                  <IconButton
                                    onClick={() => handleDelete(plan._id)}
                                    color="error"
                                  >
                                    <DeleteIcon />
                                  </IconButton>
                                </Tooltip>
                              </TableCell>
                            </TableRow>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                    </TableBody>
                  )}
                </Droppable>
              </Table>
            </TableContainer>
          </DragDropContext>
        </CardContent>
      </Card>

      {/* Create/Edit/Clone Dialog */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {dialogMode === "create" && "Create New Pricing Plan"}
          {dialogMode === "edit" && "Edit Pricing Plan"}
          {dialogMode === "clone" && "Clone Pricing Plan"}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Plan Name"
                  value={formData.name}
                  onChange={(e) => handleFormChange("name", e.target.value)}
                  margin="normal"
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth margin="normal">
                  <InputLabel>Icon</InputLabel>
                  <Select
                    value={formData.icon}
                    label="Icon"
                    onChange={(e) => handleFormChange("icon", e.target.value)}
                  >
                    {Object.entries(MUI_ICONS).map(([key, label]) => (
                      <MenuItem key={key} value={key}>
                        {label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Description"
                  value={formData.description}
                  onChange={(e) =>
                    handleFormChange("description", e.target.value)
                  }
                  margin="normal"
                  multiline
                  rows={2}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Color"
                  type="color"
                  value={formData.color}
                  onChange={(e) => handleFormChange("color", e.target.value)}
                  margin="normal"
                  InputProps={{
                    style: { height: "56px" },
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormGroup>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formData.popular}
                        onChange={(e) =>
                          handleFormChange("popular", e.target.checked)
                        }
                      />
                    }
                    label="Mark as Popular Plan"
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formData.active}
                        onChange={(e) =>
                          handleFormChange("active", e.target.checked)
                        }
                      />
                    }
                    label="Active"
                  />
                </FormGroup>
              </Grid>

              {/* Billing Cycles */}
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                  Billing Cycles
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Grid container spacing={2}>
                  {BILLING_CYCLES.map((cycle) => (
                    <Grid item xs={12} md={4} key={cycle}>
                      <Paper sx={{ p: 2 }}>
                        <Typography variant="subtitle2" gutterBottom>
                          {cycle === "quarter"
                            ? "3 Months"
                            : cycle === "halfYear"
                            ? "6 Months"
                            : "1 Year"}
                        </Typography>
                        <TextField
                          fullWidth
                          label="Price"
                          type="number"
                          value={formData.billingCycles[cycle]?.price || 0}
                          onChange={(e) =>
                            handleBillingCycleChange(
                              cycle,
                              "price",
                              e.target.value
                            )
                          }
                          margin="dense"
                          InputProps={{ startAdornment: "₹" }}
                        />
                        <TextField
                          fullWidth
                          label="Contact Limit"
                          type="number"
                          value={
                            formData.billingCycles[cycle]?.contactLimit || 0
                          }
                          onChange={(e) =>
                            handleBillingCycleChange(
                              cycle,
                              "contactLimit",
                              e.target.value
                            )
                          }
                          margin="dense"
                        />
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
              </Grid>

              {/* Message Rates */}
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                  Message Rates
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Marketing Rate"
                      type="number"
                      value={formData.messageRates.marketing || 0}
                      onChange={(e) =>
                        handleNestedFormChange(
                          "messageRates",
                          "marketing",
                          e.target.value
                        )
                      }
                      margin="normal"
                      InputProps={{ startAdornment: "₹" }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Utility Rate"
                      type="number"
                      value={formData.messageRates.utility || 0}
                      onChange={(e) =>
                        handleNestedFormChange(
                          "messageRates",
                          "utility",
                          e.target.value
                        )
                      }
                      margin="normal"
                      InputProps={{ startAdornment: "₹" }}
                    />
                  </Grid>
                </Grid>
              </Grid>

              {/* Features */}
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                  Features
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Stack spacing={2}>
                  <Box display="flex" gap={1}>
                    <TextField
                      fullWidth
                      label="New Feature"
                      value={formData.newFeature}
                      onChange={(e) =>
                        handleFormChange("newFeature", e.target.value)
                      }
                      onKeyPress={(e) =>
                        e.key === "Enter" && handleAddFeature()
                      }
                    />
                    <Button variant="outlined" onClick={handleAddFeature}>
                      Add
                    </Button>
                  </Box>
                  <Box>
                    {formData.features.map((feature, index) => (
                      <Chip
                        key={index}
                        label={feature}
                        onDelete={() => handleRemoveFeature(index)}
                        sx={{ m: 0.5 }}
                      />
                    ))}
                  </Box>
                </Stack>
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained" color="primary">
            {dialogMode === "create" && "Create Plan"}
            {dialogMode === "edit" && "Save Changes"}
            {dialogMode === "clone" && "Create Clone"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default PricingPlansManager;
